import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit,
  SimpleChanges
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  ComponentDestroyObserver,
  whileComponentNotDestroyed
} from '../../decorators/component-destroy-observer/component-destroy-observer';

import { ScrollableDirective, ScrollableState } from '../../directives/scrollable/scrollable.directive';

@Component({
  selector: 'gxs-scrollbar',
  template: `<span class="scrollbar" [ngClass]="['scrollbar_theme_' + theme]">
  <div *ngIf="state?.vertical"
       class="scrollbar__track scrollbar__track_position_vertical"
       [class.scrollbar__track_visible]="visibleVertical">
    <div class="scrollbar__knob scrollbar__knob_position_vertical"
         [style.top.%]="verticalTopPercentage"
         [style.height.%]="verticalHeightPercentage">
    </div>
  </div>
  <div *ngIf="state?.horizontal"
       class="scrollbar__track scrollbar__track_position_horizontal"
       [class.scrollbar__track_visible]="visibleHorizontal">
    <div class="scrollbar__knob scrollbar__knob_position_horizontal"
         [style.left.%]="horizontalLeftPercentage"
         [style.width.%]="horizontalWidthPercentage">
    </div>
  </div>
</span>
`,
  styles: [`:host{box-sizing:border-box}:host *,:host ::after,:host ::before{box-sizing:inherit}:host a,:host a:hover,:host a:visited{text-decoration:none}.scrollbar_theme_default .scrollbar__track{position:absolute;background:#ccc;overflow:hidden;opacity:0;transition:opacity .3s ease}.scrollbar_theme_default .scrollbar__track_visible{opacity:1}.scrollbar_theme_default .scrollbar__track_position_vertical{top:0;right:0;bottom:0;width:3px}.scrollbar_theme_default .scrollbar__track_position_horizontal{right:0;bottom:0;left:0;height:3px}.scrollbar_theme_default .scrollbar__knob{position:absolute;top:50%;width:3px;height:10px;background:#000}.scrollbar_theme_default .scrollbar__knob_position_vertical{top:50%;width:3px;height:10px}.scrollbar_theme_default .scrollbar__knob_position_horizontal{left:50%;width:10px;height:3px}`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@ComponentDestroyObserver
export class ScrollbarComponent implements OnInit, OnDestroy, OnChanges {

  @Input() scrollable: ScrollableDirective;
  @Input() theme = 'default';
  @Input() hideTimeout = 300;

  state: ScrollableState;
  subscription: Subscription;
  stateUpdated = new Subject<void>();
  stateUpdatedRecently = false;

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit() {
    if (this.hideTimeout == undefined) {
      return;
    }

    this.stateUpdated
      .pipe(
        debounceTime(this.hideTimeout),
        whileComponentNotDestroyed(this)
      )
      .subscribe(() => {
        this.stateUpdatedRecently = false;
        this.cd.detectChanges();
      });
  }

  ngOnDestroy(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scrollable']) {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }

      if (this.scrollable) {
        this.subscription = this.scrollable.state$
          .pipe(whileComponentNotDestroyed(this))
          .subscribe(state => {
            this.state = state;
            this.stateUpdatedRecently = true;
            this.stateUpdated.next();
            this.cd.detectChanges();
          });
      } else {
        this.subscription = undefined;
      }
    }
  }

  get visibleVertical() {
    if (!this.state || !this.state.vertical) {
      return false;
    }

    if (this.verticalHeightPercentage == 100) {
      return false;
    }

    if (!this.stateUpdatedRecently) {
      return false;
    }

    return true;
  }

  get verticalHeightPercentage() {
    const value = this.state.vertical.viewportLength / this.state.vertical.scrollLength * 100;
    return Math.max(value, 5);
  }

  get verticalTopPercentage() {
    return this.state.vertical.progress * (100 - this.verticalHeightPercentage);
  }

  get visibleHorizontal() {
    if (!this.state || !this.state.horizontal) {
      return false;
    }

    if (this.horizontalWidthPercentage == 100) {
      return false;
    }

    if (!this.stateUpdatedRecently) {
      return false;
    }

    return true;
  }

  get horizontalWidthPercentage() {
    const value = this.state.horizontal.viewportLength / this.state.horizontal.scrollLength * 100;
    return Math.max(value, 5);
  }

  get horizontalLeftPercentage() {
    return this.state.horizontal.progress * (100 - this.horizontalWidthPercentage);
  }
}
